import { useCallback, useState } from 'react';
import type { TomLiveMessage, TomMessage } from '../types';

const TERMINAL_MESSAGE_STATUSES = new Set(['completed', 'failed', 'cancelled']);

export const isTerminalTomMessage = (message: TomMessage) =>
	TERMINAL_MESSAGE_STATUSES.has(message.status);

export const useTomMessageOverlays = () => {
	const [messageOverlays, setMessageOverlays] = useState<
		Record<number, Record<number, TomLiveMessage>>
	>({});

	const upsertMessage = useCallback(
		(targetConversationId: number, message: TomLiveMessage) => {
			setMessageOverlays(current => ({
				...current,
				[targetConversationId]: {
					...(current[targetConversationId] || {}),
					[message.id]: message,
				},
			}));
		},
		[],
	);

	const upsertMessages = useCallback(
		(targetConversationId: number, messages: TomLiveMessage[]) => {
			setMessageOverlays(current => {
				const conversationOverlays = {
					...(current[targetConversationId] || {}),
				};
				messages.forEach(message => {
					conversationOverlays[message.id] = message;
				});

				return {
					...current,
					[targetConversationId]: conversationOverlays,
				};
			});
		},
		[],
	);

	const appendDelta = useCallback(
		(targetConversationId: number, messageId: number, content: string) => {
			setMessageOverlays(current => {
				const conversationOverlays =
					current[targetConversationId] || {};
				const existing = conversationOverlays[messageId];
				if (!existing) {
					return current;
				}

				return {
					...current,
					[targetConversationId]: {
						...conversationOverlays,
						[messageId]: {
							...existing,
							content: `${existing.content || ''}${content}`,
						},
					},
				};
			});
		},
		[],
	);

	const removeResolvedMessageOverlays = useCallback(
		(targetConversationId: number, canonicalMessages: TomMessage[]) => {
			setMessageOverlays(current => {
				const conversationOverlays = current[targetConversationId];
				if (!conversationOverlays) {
					return current;
				}

				const nextConversationOverlays = { ...conversationOverlays };
				let hasChanges = false;
				canonicalMessages.forEach(message => {
					const overlay = nextConversationOverlays[message.id];
					const canUseCanonicalMessage =
						overlay &&
						!overlay.diagnostics &&
						(message.role === 'user' ||
							(isTerminalTomMessage(message) &&
								isTerminalTomMessage(overlay)));

					if (canUseCanonicalMessage) {
						delete nextConversationOverlays[message.id];
						hasChanges = true;
					}
				});

				if (!hasChanges) {
					return current;
				}

				const nextOverlays = { ...current };
				if (Object.keys(nextConversationOverlays).length) {
					nextOverlays[targetConversationId] =
						nextConversationOverlays;
				} else {
					delete nextOverlays[targetConversationId];
				}

				return nextOverlays;
			});
		},
		[],
	);

	const clearConversationOverlays = useCallback(
		(targetConversationId: number) => {
			setMessageOverlays(current => {
				if (!current[targetConversationId]) {
					return current;
				}

				const nextOverlays = { ...current };
				delete nextOverlays[targetConversationId];
				return nextOverlays;
			});
		},
		[],
	);

	return {
		messageOverlays,
		upsertMessage,
		upsertMessages,
		appendDelta,
		removeResolvedMessageOverlays,
		clearConversationOverlays,
	};
};
